<template>
    <div class="mt-6 content">
        <div class="columns is-gapless is-mobile">
            <div class="column is-narrow"><i class="fad fa-fw fa-clipboard-list-check has-text-white mr-3 is-size-4"></i></div>
            <div class="column">
                <h3 class="has-text-white">Course Study Plan</h3>
                <p class="has-text-white">Your study plan defines the goals and objectives you'll work toward, how your hours are allocated, and how you'll evaluate your learning for this course.</p>
            </div>
        </div>

        <div class="mt-6 pb-6">
        <!-- Loading State -->
        <div class="study-plan-content-frame" v-if="loading">
            <div class="py-2 has-text-centered">
                <i class="fas fa-spin fa-spinner fa-fw mr-2"></i>Loading study plan...
            </div>
        </div>

        <!-- Pending Approval State -->
        <template v-else-if="setupStatus === 'submitted'">
            <div class="study-plan-content-frame">
                <div class="has-text-centered py-6">
                    <i class="fas fa-clock fa-4x has-text-warning mb-4"></i>
                    <h2 class="mb-3">Course Study Plan Pending Approval</h2>
                    <p class="has-text-grey mb-4" style="max-width: 680px; margin: 0 auto;">
                        Your course study plan has been submitted and is awaiting approval, typically within 24-48 hours.
                        You will receive an email notification once your plan has been reviewed.
                    </p>
                    <p class="is-size-7 has-text-grey mt-5" v-if="setupData.submitted_at">
                        Submitted: {{ formatDate(setupData.submitted_at) }}
                    </p>
                </div>
            </div>
        </template>

        <!-- Approved State -->
        <template v-else-if="setupStatus === 'approved'">
            <div class="study-plan-content-frame approved-content">
                <!-- Tabs for Syllabus and Plan -->
                <b-tabs v-model="approvedTab" :animated="false" class="mb-4">
                    <!-- Syllabus is optional: nothing generates one asynchronously, so the
                         tab only exists when a syllabus was actually stored (imported
                         classic enrollments never have one) -->
                    <b-tab-item v-if="setupData.syllabus" label="Your Syllabus" icon="list-alt">
                        <div class="ai-content" v-html="setupData.syllabus"></div>
                    </b-tab-item>
                    <b-tab-item label="Your Plan" icon="clipboard-list">
                        <div class="plan-summary">
                            <div class="mb-5 pb-4" style="border-bottom: 1px solid #eee;">
                                <span class="tag is-success is-light">
                                    <i class="fas fa-check-circle mr-2"></i>Approved
                                </span>
                                <span class="tag is-success is-light ml-2" v-if="setupData.stem_agreed">
                                    <i class="fas fa-flask mr-2"></i>STEM Clock Hours
                                </span>
                                <span class="is-size-7 has-text-grey ml-3" v-if="setupData.approved_at">
                                    {{ formatDate(setupData.approved_at) }}
                                </span>
                                <span class="is-size-7 has-text-grey ml-3" v-if="setupData.submitted_at">
                                    Submitted {{ formatDate(setupData.submitted_at) }}
                                </span>
                            </div>
                            <div class="mb-5 p-4" style="background-color: #f8f9fa; border-radius: 8px;">
                                <div class="has-text-weight-bold">{{ planCourseTitle }}</div>
                                <div class="is-size-7 has-text-grey mt-1" v-if="setupData.custom_course_name && course.title">
                                    {{ course.title }}
                                </div>
                                <div class="is-size-7 has-text-grey mt-2">
                                    <span class="mr-4"><i class="far fa-clock mr-1"></i>{{ enrolledHours }} enrolled hours</span>
                                    <span v-if="enrollment && enrollment.created_at">
                                        <i class="far fa-calendar mr-1"></i>Enrolled {{ formatDate(enrollment.created_at) }}
                                    </span>
                                </div>
                            </div>
                            <template v-if="setupData.resume && setupData.resume.background">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Background</h5>
                                <p class="mb-4" style="white-space: pre-line;">{{ setupData.resume.background }}</p>
                            </template>
                            <template v-if="setupData.resume && setupData.resume.work_history">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Work History</h5>
                                <p class="mb-4" style="white-space: pre-line;">{{ setupData.resume.work_history }}</p>
                            </template>
                            <template v-if="setupData.resume && setupData.resume.importance">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Why This Subject Matters</h5>
                                <p class="mb-4" style="white-space: pre-line;">{{ setupData.resume.importance }}</p>
                            </template>
                            <template v-if="setupData.custom_description">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Course Description</h5>
                                <p class="mb-4" style="white-space: pre-line;">{{ setupData.custom_description }}</p>
                            </template>
                            <h5 class="mb-3" style="margin-top: 1.375rem;">Goals</h5>
                            <ul class="mb-4">
                                <li v-for="(goal, i) in allGoals" :key="'goal-'+i">{{ goal }}</li>
                            </ul>
                            <h5 class="mb-3" style="margin-top: 1.375rem;">Objectives & Hours</h5>
                            <ul class="mb-4">
                                <li v-for="(obj, i) in allObjectivesWithText" :key="'obj-'+i">
                                    {{ obj.text }} <span class="tag is-info is-light ml-2">{{ setupData.hour_allocations[obj.id] || 0 }} hrs</span>
                                </li>
                            </ul>
                            <template v-if="applicationLevelLabels">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Application Level</h5>
                                <p class="mb-4">{{ applicationLevelLabels }}</p>
                            </template>
                            <template v-if="setupData.cumulative_product">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Cumulative Product</h5>
                                <p class="mb-4" style="white-space: pre-line;">{{ setupData.cumulative_product }}</p>
                            </template>
                            <template v-if="setupData.other_materials">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Additional Materials</h5>
                                <p class="mb-4" style="white-space: pre-line;">{{ setupData.other_materials }}</p>
                            </template>
                            <template v-if="setupData.material_location">
                                <h5 class="mb-3" style="margin-top: 1.375rem;">Materials Location</h5>
                                <p class="mb-4" style="white-space: pre-line;">{{ setupData.material_location }}</p>
                            </template>
                            <h5 class="mb-3" style="margin-top: 1.375rem;">Evaluation Plan</h5>
                            <p style="white-space: pre-line;">{{ setupData.evaluation_plan }}</p>
                        </div>
                    </b-tab-item>
                </b-tabs>

            </div>
        </template>

        <!-- Wizard Form -->
        <template v-else>
            <div class="study-plan-content-frame">
                <!-- Header -->
                <div class="study-plan-header">
                    <img v-if="enrollment && enrollment.course && enrollment.course.logo" :src="enrollment.course.logo" style="height: 70px;"/>

                    <div class="mt-4 mb-4">
                        <div class="has-text-grey-dark">
                            <strong class="has-text-grey-dark">Develop Your Course Study Plan</strong> -
                            Step {{ activeStep + 1 }} of 4
                        </div>
                        <h2 class="mt-2">{{ stepTitles[activeStep] }}</h2>
                    </div>

                    <!-- Progress bar -->
                    <div class="mb-4">
                        <b-progress :value="((activeStep + 1) / 4) * 100" type="is-info" size="is-small"></b-progress>
                    </div>
                </div>

                <!-- Admin feedback message for changes_requested -->
                <b-message v-if="setupStatus === 'changes_requested' && setupData.admin_feedback" type="is-warning" class="mb-5">
                    <div class="columns is-gapless">
                        <div class="column is-narrow"><i class="fas fa-triangle-exclamation mr-3"></i></div>
                        <div class="column">
                            <strong>Changes Requested</strong>
                            <p class="mt-2">{{ setupData.admin_feedback }}</p>
                        </div>
                    </div>
                </b-message>

                <!-- Step Content -->
                <div class="step-content">
                    <!-- Step 1: Resume -->
                    <div v-show="activeStep === 0">
                        <!-- How the Study Plan works (adapted from the legacy post-enrollment
                             instructions) -->
                        <div class="wizard-section">
                            <p class="is-size-6 has-text-grey mb-3">
                                This Study Plan has four short sections &mdash; <strong>Your Background</strong>,
                                <strong>Goals &amp; Objectives</strong>, <strong>Hour Allocation</strong>, and
                                <strong>Finalize Your Plan</strong> &mdash; that outline how you'll focus your study.
                                Choose from the topics and areas we provide, or add your own; all customizable areas
                                are optional.
                            </p>
                            <p class="is-size-6 has-text-grey">
                                Once you submit, your plan is reviewed and approved &mdash; typically within 24-48
                                hours &mdash; and your course becomes active. From there you'll complete the
                                activities you outlined here and record your time in your Study Log. Take your time
                                on this plan: you can save a draft and come back before submitting, but once it's
                                approved it's locked in and can no longer be changed.
                            </p>
                            <p class="is-size-6 has-text-weight-bold mt-3">
                                You cannot begin coursework until your plan is approved &mdash; submit it as soon as
                                you're ready so review doesn't hold up your start.
                            </p>
                        </div>

                        <div class="wizard-section">
                            <div class="flat-box" style="background-color: #fff8e6; border: 1px solid #ffc107;">
                                <h5 class="mb-3"><i class="far fa-info-circle mr-2"></i>Important Course Expectations</h5>
                                <p class="mb-3">
                                    Once your course is underway, here's what's expected of you:
                                </p>
                                <ul class="ml-5">
                                    <li class="mb-2">
                                        Reflections and artifacts need to be complete, thorough, and authentic, and
                                        should map directly to your goals and objectives.
                                    </li>
                                    <li class="mb-2">
                                        Time you're paid to do as part of your contract (excluding prep time) may not
                                        be counted in your Study Log.
                                    </li>
                                    <li class="mb-2">Upload supporting files or artifacts to your reflections as needed or requested.</li>
                                    <li class="mb-2">
                                        Incomplete or inadequate reflections, or missing artifacts, may be rejected
                                        and require resubmission before clock hours are awarded.
                                    </li>
                                    <li>
                                        You'll receive an email notification if changes are requested &mdash; review
                                        the feedback and resubmit promptly to avoid delays.
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <p class="mb-5 has-text-grey">Tell us about yourself and why this course is relevant to you.</p>

                        <div class="wizard-section">
                            <b-field message="Describe your current role and professional experience (minimum 50 characters)">
                                <template #label>Professional Background <span class="has-text-danger">*</span></template>
                                <b-input
                                    type="textarea"
                                    v-model="setupData.resume.background"
                                    placeholder="Describe your current position, years of experience, and relevant background..."
                                    rows="4"
                                ></b-input>
                            </b-field>
                            <div class="is-size-7 has-text-right" :class="{'has-text-danger': charCount('resume.background') < 50, 'has-text-success': charCount('resume.background') >= 50}">
                                {{ charCount('resume.background') }} / 50 min characters
                            </div>
                        </div>

                        <div class="wizard-section">
                            <b-field message="Describe your relevant work history and experience (minimum 50 characters)">
                                <template #label>Work History <span class="has-text-danger">*</span></template>
                                <b-input
                                    type="textarea"
                                    v-model="setupData.resume.work_history"
                                    placeholder="List previous relevant positions and experiences..."
                                    rows="4"
                                ></b-input>
                            </b-field>
                            <div class="is-size-7 has-text-right" :class="{'has-text-danger': charCount('resume.work_history') < 50, 'has-text-success': charCount('resume.work_history') >= 50}">
                                {{ charCount('resume.work_history') }} / 50 min characters
                            </div>
                        </div>

                        <div class="wizard-section">
                            <b-field message="Explain why this subject is important to your professional development (minimum 50 characters)">
                                <template #label>Why This Subject Matters <span class="has-text-danger">*</span></template>
                                <b-input
                                    type="textarea"
                                    v-model="setupData.resume.importance"
                                    placeholder="Explain how this course will benefit your professional growth..."
                                    rows="4"
                                ></b-input>
                            </b-field>
                            <div class="is-size-7 has-text-right" :class="{'has-text-danger': charCount('resume.importance') < 50, 'has-text-success': charCount('resume.importance') >= 50}">
                                {{ charCount('resume.importance') }} / 50 min characters
                            </div>
                        </div>
                    </div>

                    <!-- Step 2: Overview -->
                    <div v-show="activeStep === 1">
                        <p class="mb-5 has-text-grey">
                            In this section, you will outline the direction of your learning. You may customize the
                            course by adding your own elements, and/or choose from those we provide. Your selections
                            in this section will determine the activities and tasks you will complete as a part of
                            this course.
                        </p>

                        <!-- Course Info -->
                        <div class="flat-box mb-5" style="background-color: #f8f9fa;">
                            <div class="has-text-weight-bold">{{ course.title }}</div>
                            <div class="is-size-7 has-text-grey mt-1" v-if="course.description">{{ course.description }}</div>
                        </div>

                        <!-- Custom Description (Optional) -->
                        <b-field label="Custom Description (Optional)" class="wizard-section">
                            <template #message>
                                Do you want to customize your course's description by adding any additional or specific information?
                            </template>
                            <b-input
                                v-model="setupData.custom_description"
                                type="textarea"
                                rows="2"
                                placeholder="Add any additional or specific information about your course..."
                            ></b-input>
                        </b-field>

                        <!-- Goals -->
                        <div class="wizard-section">
                            <label class="label">Goals <span class="has-text-danger">*</span></label>
                            <p class="is-size-6 has-text-grey mb-3">
                                Choose the goals you'd like to accomplish during your {{ course.title }} course.
                                Goals are high-level principles that direct your study.
                                <strong>You need at least {{ minGoals }} goals. Select or add your own.</strong>
                            </p>

                            <div v-if="courseGoals.length > 0" class="mb-3">
                                <div class="is-size-6 has-text-weight-bold mb-2">During this course, you will</div>
                                <div v-for="(goal, index) in courseGoals" :key="'goal-' + index" class="mb-2">
                                    <b-checkbox v-model="setupData.selected_goals" :native-value="goal.id || goal">
                                        {{ goal.text || goal }}
                                    </b-checkbox>
                                </div>
                            </div>
                            <div v-else class="has-text-grey is-size-6 mb-3">No predefined goals available</div>

                            <!-- Custom goals -->
                            <div class="mt-4">
                                <p class="is-size-6 has-text-grey mb-2">
                                    Do you want to add any goals to your course? Remember, goals are high-level
                                    principles that direct your study.
                                </p>
                                <label class="label">Custom Goals</label>
                                <div v-for="(goal, index) in setupData.custom_goals" :key="'custom-goal-' + index" class="mb-2">
                                    <div class="columns is-gapless is-mobile">
                                        <div class="column">
                                            <b-input v-model="setupData.custom_goals[index]" size="is-small"></b-input>
                                        </div>
                                        <div class="column is-narrow ml-2">
                                            <b-button type="is-danger" size="is-small" icon-left="times" @click="removeCustomGoal(index)"></b-button>
                                        </div>
                                    </div>
                                </div>
                                <b-button type="is-light" size="is-small" icon-left="plus" @click="addCustomGoal">Add Custom Goal</b-button>
                            </div>
                            <div class="is-size-6 mt-2" :class="{'has-text-danger': totalGoals < minGoals, 'has-text-success': totalGoals >= minGoals}">
                                {{ totalGoals }} / {{ minGoals }} goals selected
                            </div>
                        </div>

                        <!-- Objectives -->
                        <div class="wizard-section">
                            <label class="label">Objectives <span class="has-text-danger">*</span></label>
                            <p class="is-size-6 has-text-grey mb-3">
                                Choose the objectives you'd like to accomplish during your {{ course.title }} course.
                                Objectives are specific, measurable steps designed to meet your selected goals.
                                <strong>You need at least {{ minObjectives }} objectives. Select or add your own.</strong>
                            </p>

                            <b-message type="is-info" size="is-small" class="mb-4" :closable="false">
                                <strong><i class="far fa-info-circle mr-1"></i> TIP:</strong> the objectives you
                                select below will determine the tasks, assignments, and activities you'll complete
                                during this course for clock hours.
                            </b-message>

                            <div v-if="courseObjectives.length > 0" class="mb-3">
                                <div class="is-size-6 has-text-weight-bold mb-2">By the end of this course, you will have completed the following:</div>
                                <div v-for="(objective, index) in courseObjectives" :key="'obj-' + index" class="mb-2">
                                    <b-checkbox v-model="setupData.selected_objectives" :native-value="objective.id || objective">
                                        {{ objective.text || objective }}
                                        <span v-if="objective.stem" class="tag is-success is-light ml-1">STEM</span>
                                    </b-checkbox>
                                </div>
                            </div>
                            <div v-else class="has-text-grey is-size-6 mb-3">No predefined objectives available</div>

                            <!-- Custom objectives -->
                            <div class="mt-4">
                                <p class="is-size-6 has-text-grey mb-2">
                                    Do you want to add any objectives to your course? Remember, objectives are
                                    specific, measurable steps designed to meet the goals.
                                    <strong>Each objective you add will be included in the list above.</strong>
                                </p>
                                <label class="label">Custom Objectives</label>
                                <div v-for="(obj, index) in setupData.custom_objectives" :key="'custom-obj-' + index" class="mb-2">
                                    <div class="columns is-gapless is-mobile">
                                        <div class="column">
                                            <b-input v-model="setupData.custom_objectives[index]" size="is-small"></b-input>
                                        </div>
                                        <div class="column is-narrow ml-2">
                                            <b-button type="is-danger" size="is-small" icon-left="times" @click="removeCustomObjective(index)"></b-button>
                                        </div>
                                    </div>
                                </div>
                                <b-button type="is-light" size="is-small" icon-left="plus" @click="addCustomObjective">Add Custom Objective</b-button>
                            </div>
                            <div class="is-size-6 mt-2" :class="{'has-text-danger': totalObjectives < minObjectives, 'has-text-success': totalObjectives >= minObjectives}">
                                {{ totalObjectives }} / {{ minObjectives }} objectives selected
                            </div>
                        </div>

                        <!-- STEM Clock Hour Requirements (shown when a STEM objective is selected) -->
                        <div v-if="stemObjectiveSelected" class="wizard-section">
                            <div class="flat-box stem-info-box" style="background-color: #f0f7f0; border: 1px solid #347f31;">
                                <h5 class="mb-3"><i class="fas fa-flask mr-2"></i>STEM Clock Hour Requirements</h5>
                                <p class="mb-2"><strong>Criteria:</strong></p>
                                <ol class="ml-5 mb-4">
                                    <li class="mb-2">
                                        The intent of the RCW is to ensure students have exposure to authentic STEM integration
                                        experiences which align to state learning standards, including information about STEM-related
                                        career choices. The intent is for educators to incorporate the learning from the STEM activity
                                        within their professional practice such as a classroom or professional development opportunity
                                        (PLC, staff meeting, district-level workshop, etc.).
                                    </li>
                                    <li>
                                        The educator must participate in or demonstrate implementation of a STEM activity. The learning
                                        or activity must demonstrate authentic integration of science, technology, engineering and math,
                                        incorporating at least 2 of the 4 STEM elements. Only one element out of the STEM learning
                                        experience is not considered an authentic STEM experience.
                                    </li>
                                </ol>
                                <p class="mb-2">
                                    <strong>Guiding Questions:</strong> In order for this STEM-related continuing education course
                                    offering to be acceptable and/or awarded to the educator, the course provider/reviewer/instructor
                                    may ask for all STEM-related materials necessary to ensure the course meets the renewal requirement,
                                    and each of the following must be true:
                                </p>
                                <ol class="ml-5 mb-4">
                                    <li>Will the STEM activity have an impact on STEM experiences for students?</li>
                                    <li>Does the STEM activity provide examples or resources to use with students or with other educators?</li>
                                    <li>Does the STEM activity provide examples or resources about STEM-related career choices to use with students?</li>
                                </ol>
                                <p class="mb-4">
                                    For more information on state requirements visit the
                                    <a href="https://ospi.k12.wa.us" target="_blank" rel="noopener">OSPI website</a>.
                                </p>
                                <div class="flat-box mb-0" style="background-color: #fff8e6; border: 1px solid #ffc107;">
                                    <b-checkbox v-model="setupData.stem_agreed" class="agreement-checkbox">
                                        <strong>Count my qualifying hours as STEM clock hours</strong>
                                        <p class="has-text-grey mt-1">
                                            I understand and agree that this course may require additional materials and/or examples in
                                            order to qualify for STEM clock hours per the OSPI requirements. I understand and agree that
                                            if, after two (2) attempts by staff requesting additional materials and/or artifacts that
                                            clearly demonstrate and fulfill the STEM requirements, I may be awarded clock hours but not
                                            STEM clock hours.
                                        </p>
                                    </b-checkbox>
                                    <p class="has-text-grey mt-2">
                                        Opting in is not a commitment &mdash; if you do not complete STEM activities, your standard
                                        reflections and artifacts are all that is required.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- Application Level -->
                        <div class="wizard-section">
                            <label class="label">Application Level <span class="has-text-danger">*</span></label>
                            <p class="is-size-6 has-text-grey mb-3">Select the level(s) at which you will apply this learning</p>

                            <div v-for="level in applicationLevels" :key="level.value" class="mb-2">
                                <b-checkbox v-model="setupData.application_level" :native-value="level.value">
                                    <strong>{{ level.label }}</strong> - {{ level.description }}
                                </b-checkbox>
                            </div>
                            <div class="is-size-6 mt-2" :class="{'has-text-danger': !setupData.application_level.length, 'has-text-success': setupData.application_level.length > 0}">
                                {{ setupData.application_level.length }} level(s) selected
                            </div>
                        </div>
                    </div>

                    <!-- Step 3: Agenda -->
                    <div v-show="activeStep === 2">
                        <p class="mb-5 has-text-grey">Allocate your enrolled hours across your selected objectives.</p>

                        <!-- Hours summary -->
                        <div class="flat-box mb-5" style="background-color: #f8f9fa;">
                            <div class="columns is-mobile">
                                <div class="column has-text-centered">
                                    <div class="is-size-3 has-text-weight-bold has-text-primary">{{ enrolledHours }}</div>
                                    <div class="is-size-7 has-text-grey">Enrolled Hours</div>
                                </div>
                                <div class="column has-text-centered">
                                    <div class="is-size-3 has-text-weight-bold" :class="{'has-text-success': allocatedHours === enrolledHours, 'has-text-warning': allocatedHours !== enrolledHours}">{{ allocatedHours }}</div>
                                    <div class="is-size-7 has-text-grey">Allocated Hours</div>
                                </div>
                                <div class="column has-text-centered">
                                    <div class="is-size-3 has-text-weight-bold" :class="{'has-text-success': remainingHours === 0, 'has-text-warning': remainingHours > 0}">{{ remainingHours }}</div>
                                    <div class="is-size-7 has-text-grey">Remaining</div>
                                </div>
                            </div>
                        </div>

                        <div v-if="allObjectivesWithText.length === 0" class="notification is-warning">
                            <i class="fas fa-exclamation-triangle mr-2"></i>
                            Please select objectives in the previous step first.
                        </div>

                        <div v-else>
                            <div v-for="(objective, index) in allObjectivesWithText" :key="'alloc-' + index" class="mb-5">
                                <label class="label is-size-6">{{ objective.text }}</label>
                                <div class="columns is-mobile is-vcentered">
                                    <div class="column">
                                        <b-slider
                                            v-model="setupData.hour_allocations[objective.id]"
                                            :min="0"
                                            :max="Math.max(maxFor(objective.id), 0.5)"
                                            :step="0.5"
                                            :disabled="maxFor(objective.id) < 0.5"
                                            type="is-info"
                                            @input="clampAllocation(objective.id)"
                                        ></b-slider>
                                    </div>
                                    <div class="column is-narrow" style="width: 100px;">
                                        <b-input
                                            type="number"
                                            v-model.number="setupData.hour_allocations[objective.id]"
                                            :min="0"
                                            :max="maxFor(objective.id)"
                                            step="0.5"
                                            :disabled="maxFor(objective.id) < 0.5"
                                            size="is-small"
                                            @input="clampAllocation(objective.id)"
                                        ></b-input>
                                    </div>
                                    <div class="column is-narrow" style="min-width: 130px;">
                                        <span class="is-size-7 has-text-grey">of {{ maxFor(objective.id) }} available</span>
                                    </div>
                                </div>
                            </div>

                            <div class="wizard-section">
                                <p class="is-size-6 has-text-weight-bold mb-2">Note:</p>
                                <ul class="is-size-6 has-text-grey ml-5">
                                    <li>Hours can be entered in 0.5-hour increments.</li>
                                    <li>The total number of hours assigned must add up to {{ enrolledHours }}, the number of hours you enrolled in.</li>
                                    <li>You must assign hours to each objective.</li>
                                    <li>The Allocated Hours count above turns green once all your hours are assigned.</li>
                                </ul>
                            </div>

                            <div class="wizard-section">
                                <div class="flat-box" style="background-color: #fff8e6; border: 1px solid #ffc107;">
                                    <p>
                                        <strong><i class="far fa-hand-point-right mr-2"></i>TIP:</strong> Too many objectives
                                        and not enough clock hours? Two options:
                                    </p>
                                    <ol class="ml-5 mt-2">
                                        <li>Go back to <strong>Goals &amp; Objectives</strong> and remove some of your selected objectives.</li>
                                        <li>
                                            <router-link :to="{name: 'support', params: {view: 'new'}}" target="_blank">Contact our support team</router-link> &mdash;
                                            we may be able to enroll you in an additional course to continue your work.
                                        </li>
                                    </ol>
                                </div>
                            </div>

                            <b-field label="Cumulative Product (Optional)" class="wizard-section">
                                <template #message>
                                    Will you have a cumulative product from your {{ course.title }} study during this course? If so, please define it here.
                                </template>
                                <b-input
                                    type="textarea"
                                    v-model="setupData.cumulative_product"
                                    rows="3"
                                    placeholder="Describe the cumulative product you'll create during this course, if any..."
                                ></b-input>
                            </b-field>
                        </div>

                        <b-message v-if="remainingHours > 0 && allObjectivesWithText.length > 0" type="is-warning" class="mt-5">
                            <i class="fas fa-exclamation-triangle mr-2"></i>
                            You have {{ remainingHours }} {{ remainingHours === 1 ? 'hour' : 'hours' }} remaining to allocate.
                            All {{ enrolledHours }} enrolled hours must be assigned to an objective before you can continue.
                        </b-message>
                    </div>

                    <!-- Step 4: Specifics -->
                    <div v-show="activeStep === 3">
                        <p class="mb-5 has-text-grey">Review materials, resources, and finalize your study plan.</p>

                        <!-- Time Requirement -->
                        <div class="wizard-section">
                            <p class="is-size-6 has-text-grey mb-2">
                                You've selected to earn <strong>{{ enrolledHours }}</strong> clock hours for this
                                course. Clock hours equal &ldquo;seat time,&rdquo; so plan to spend {{ enrolledHours }}
                                hours on your study &mdash; online and offline, completing the activities you defined
                                in Hour Allocation. You'll record your time in the Study Log once your course is active.
                            </p>
                            <p class="is-size-6 has-text-grey">
                                <strong>Note:</strong> If you are a Washington State school district employee, these
                                clock hours must be completed during non-contracted time, including before and after
                                your contracted work day, weekends, holidays, etc.
                            </p>
                        </div>

                        <!-- Materials (display only) -->
                        <div class="wizard-section" v-if="courseMaterials.length > 0">
                            <label class="label">Required Materials</label>
                            <p class="is-size-6 has-text-grey mb-2">The materials you'll need vary depending on the objectives you selected. You may need:</p>
                            <ul class="ml-4">
                                <li v-for="(material, index) in courseMaterials" :key="'mat-' + index">{{ material.text || material.title || material }}</li>
                            </ul>
                        </div>

                        <!-- Resources (display only) -->
                        <div class="wizard-section" v-if="courseResources.length > 0">
                            <label class="label">Resources</label>
                            <p class="is-size-6 has-text-grey mb-2">While working on this course, you may find the following resources helpful:</p>
                            <ul class="ml-4">
                                <li v-for="(resource, index) in courseResources" :key="'res-' + index">
                                    <a v-if="resource.url" :href="resource.url" target="_blank">{{ resource.title }}</a>
                                    <span v-else>{{ resource.title || resource }}</span>
                                </li>
                            </ul>
                        </div>

                        <!-- Other Materials -->
                        <div class="wizard-section">
                            <b-field label="Additional Materials (Optional)" message="Are there other materials you'll need to gather to meet your goals and objectives?">
                                <b-input
                                    type="textarea"
                                    v-model="setupData.other_materials"
                                    placeholder="List any additional books, websites, or resources you plan to use..."
                                    rows="3"
                                ></b-input>
                            </b-field>
                            <b-field label="Where Will These Materials Come From? (Optional)" class="mt-4">
                                <b-input
                                    type="textarea"
                                    v-model="setupData.material_location"
                                    placeholder="Describe where you'll get these materials (library, purchase, borrowed, online, etc.)..."
                                    rows="2"
                                ></b-input>
                            </b-field>
                        </div>

                        <!-- Evaluation Plan -->
                        <div class="wizard-section">
                            <b-field message="Describe how you will evaluate your learning (minimum 50 characters)">
                                <template #label>Evaluation Plan <span class="has-text-danger">*</span></template>
                                <b-input
                                    type="textarea"
                                    v-model="setupData.evaluation_plan"
                                    placeholder="Describe how you will measure your progress and success in this course..."
                                    rows="4"
                                ></b-input>
                            </b-field>
                            <div class="is-size-7 has-text-right" :class="{'has-text-danger': charCount('evaluation_plan') < 50, 'has-text-success': charCount('evaluation_plan') >= 50}">
                                {{ charCount('evaluation_plan') }} / 50 min characters
                            </div>
                        </div>

                        <!-- Course title completion — only on courses that grant it
                             (custom_naming.allow_custom_entry, legacy custominfo):
                             the student completes the title after the fixed prefix -->
                        <div v-if="customNaming" class="wizard-section">
                            <label class="label">Course Title <span class="has-text-danger">*</span></label>
                            <div class="is-size-6 has-text-grey mb-2" v-if="customNaming.prompt">{{ customNaming.prompt }}</div>
                            <b-field>
                                <p class="control" v-if="customNamePrefix">
                                    <span class="button is-static">{{ customNamePrefix }}</span>
                                </p>
                                <b-input
                                    v-model="customNameEntry"
                                    maxlength="45"
                                    expanded
                                    placeholder="Complete your course title..."
                                ></b-input>
                            </b-field>
                        </div>

                        <!-- Reflections Agreement -->
                        <div class="flat-box stem-info-box" style="background-color: #fff8e6; border: 1px solid #ffc107;">
                            <h5 class="mb-3"><i class="far fa-flag-swallowtail mr-2"></i>Milestone Reflections</h5>
                            <p class="mb-2">
                                This course pauses at a milestone checkpoint every 10 hours of logged study time. At each
                                checkpoint you will write a short reflection on your progress before you can continue
                                logging time.
                            </p>
                            <p class="mb-2">A thoughtful reflection typically covers:</p>
                            <ol class="ml-5 mb-4">
                                <li>What you studied and learned during this milestone period.</li>
                                <li>How you are applying (or plan to apply) that learning in your professional practice.</li>
                                <li>Any challenges you encountered and how you addressed them.</li>
                            </ol>
                            <p class="mb-4">
                                Reflections are reviewed by our staff, and you will be notified as each one is approved. You
                                can attach supporting files or artifacts to any reflection. Reflections must be authentic and
                                in your own words &mdash; repetitive or copy/pasted reflections will be rejected, and you may
                                be asked to rewrite them or provide additional artifacts. Along with your Study Log, your
                                reflections document your coursework and are required should your course be selected for audit.
                            </p>
                            <b-checkbox v-model="setupData.reflections_agreed" class="agreement-checkbox">
                                <strong>I agree to submit reflections</strong>
                                <p class="has-text-grey mt-1">
                                    I understand that I will be required to submit written reflections at each milestone to
                                    demonstrate my learning and progress, and that time logging pauses at each checkpoint
                                    until my reflection has been submitted and approved.
                                </p>
                            </b-checkbox>
                        </div>
                    </div>
                </div>
            <!-- Bottom Controls -->
            <div class="content-controls">
                <div class="columns">
                    <div class="column is-flex is-align-items-center">
                        <span class="is-size-7 has-text-grey" v-if="saving"><i class="fas fa-spinner fa-spin mr-2"></i>Saving&hellip;</span>
                        <span class="is-size-7 has-text-danger" v-else-if="saveError"><i class="fas fa-triangle-exclamation mr-2"></i>Unable to save &mdash; check your connection</span>
                        <span class="is-size-7 has-text-grey" v-else-if="lastSavedAt"><i class="fas fa-check mr-2"></i>Draft saved</span>
                    </div>
                    <div class="column has-text-right">
                        <b-button type="is-light" v-if="activeStep > 0" @click="prevStep" :disabled="saving">
                            <i class="fas fa-arrow-left mr-3"></i>Previous
                        </b-button>
                        <b-button
                            type="is-primary"
                            class="ml-3"
                            v-if="activeStep < 3"
                            @click="nextStep"
                            :disabled="!canProceed || saving"
                            :loading="saving"
                        >
                            Next Step<i class="fas fa-arrow-right ml-3"></i>
                        </b-button>
                        <b-button
                            type="is-success"
                            class="ml-3"
                            v-if="activeStep === 3"
                            @click="submitSetup"
                            :disabled="!canSubmit || saving"
                            :loading="saving"
                        >
                            Submit for Approval<i class="fas fa-paper-plane ml-3"></i>
                        </b-button>
                    </div>
                </div>
            </div>
            </div>
        </template>
        </div>
    </div>
</template>

<script>
import {client as http} from "../../http_client";
import {mapGetters} from "vuex";
import moment from "moment";

export default {
    props: ['enrollmentId'],
    data() {
        return {
            loading: true,
            saving: false,
            activeStep: 0,
            approvedTab: 0,
            course: {},
            courseConfig: {},
            maxHours: 0,
            setupData: {
                resume: {
                    background: '',
                    work_history: '',
                    importance: ''
                },
                selected_goals: [],
                custom_goals: [],
                selected_objectives: [],
                custom_objectives: [],
                application_level: [],
                hour_allocations: {},
                other_materials: '',
                material_location: '',
                cumulative_product: '',
                evaluation_plan: '',
                custom_description: '',
                custom_course_name: '',
                reflections_agreed: false,
                stem_agreed: false,
                status: 'draft'
            },
            serverSetupStatus: null,
            autosaveTimer: null,
            autosaveReady: false,
            lastSavedAt: null,
            saveError: false,
            stepTitles: [
                'Your Background',
                'Goals & Objectives',
                'Hour Allocation',
                'Finalize Your Plan'
            ],
            applicationLevels: [
                {value: 'awareness', label: 'Awareness', description: 'Basic understanding of concepts'},
                {value: 'beginning', label: 'Beginning', description: 'Initial application of skills'},
                {value: 'developing', label: 'Developing', description: 'Growing proficiency'},
                {value: 'applying', label: 'Applying', description: 'Regular use in practice'},
                {value: 'integrating', label: 'Integrating', description: 'Seamless incorporation into work'}
            ]
        };
    },
    computed: {
        ...mapGetters(['enrollment']),
        setupStatus() {
            return this.serverSetupStatus || this.enrollment?.setup_status || null;
        },
        courseGoals() {
            if (!this.courseConfig.goals) return [];
            let goals = this.courseConfig.goals;
            // Parse if it's a JSON string
            if (typeof goals === 'string') {
                try {
                    goals = JSON.parse(goals);
                } catch (e) {
                    // Not JSON, treat as newline-separated
                    return goals.split('\n').filter(g => g.trim());
                }
            }
            return Array.isArray(goals) ? goals : [];
        },
        courseObjectives() {
            if (!this.courseConfig.objectives) return [];
            let objectives = this.courseConfig.objectives;
            // Parse if it's a JSON string
            if (typeof objectives === 'string') {
                try {
                    objectives = JSON.parse(objectives);
                } catch (e) {
                    // Not JSON, treat as newline-separated
                    return objectives.split('\n').filter(o => o.trim());
                }
            }
            return Array.isArray(objectives) ? objectives : [];
        },
        courseMaterials() {
            if (!this.courseConfig.materials) return [];
            let materials = this.courseConfig.materials;
            // Parse if it's a JSON string
            if (typeof materials === 'string') {
                try {
                    materials = JSON.parse(materials);
                } catch (e) {
                    // Not JSON, treat as newline-separated
                    return materials.split('\n').filter(m => m.trim());
                }
            }
            return Array.isArray(materials) ? materials : [];
        },
        courseResources() {
            if (!this.courseConfig.resources) return [];
            let resources = this.courseConfig.resources;
            // Parse if it's a JSON string
            if (typeof resources === 'string') {
                try {
                    resources = JSON.parse(resources);
                } catch (e) {
                    return [];
                }
            }
            return Array.isArray(resources) ? resources : [];
        },
        minGoals() {
            return this.courseConfig.min_goals || 2;
        },
        minObjectives() {
            return this.courseConfig.min_objectives || 2;
        },
        totalGoals() {
            return this.setupData.selected_goals.length + this.setupData.custom_goals.filter(g => g.trim()).length;
        },
        allGoals() {
            // Map selected goal IDs to text
            const selected = this.setupData.selected_goals.map(id => {
                const goal = this.courseGoals.find(g => (g.id || g) === id);
                return goal?.text || goal || id;
            });
            // Add custom goals
            const custom = this.setupData.custom_goals.filter(g => g.trim());
            return [...selected, ...custom];
        },
        totalObjectives() {
            return this.setupData.selected_objectives.length + this.setupData.custom_objectives.filter(o => o.trim()).length;
        },
        stemObjectiveIds() {
            return this.courseObjectives.filter(o => o && o.stem === true).map(o => o.id);
        },
        stemObjectiveSelected() {
            return this.setupData.selected_objectives.some(id => this.stemObjectiveIds.includes(id));
        },
        allObjectives() {
            const selected = [...this.setupData.selected_objectives];
            const custom = this.setupData.custom_objectives.filter(o => o.trim());
            return [...selected, ...custom];
        },
        allObjectivesWithText() {
            // Map selected objective IDs to objects with id and text
            const selected = this.setupData.selected_objectives.map(id => {
                const obj = this.courseObjectives.find(o => (o.id || o) === id);
                return {
                    id: id,
                    text: obj?.text || obj || id
                };
            });
            // Map custom objectives to objects with id and text
            const custom = this.setupData.custom_objectives
                .filter(o => o.trim())
                .map((text, index) => ({
                    id: 'custom_' + index,
                    text: text
                }));
            return [...selected, ...custom];
        },
        planCourseTitle() {
            const custom = (this.setupData.custom_course_name || '').trim();
            return custom || this.course.title || '';
        },
        customNaming() {
            const naming = this.courseConfig.custom_naming;
            return (naming && naming.allow_custom_entry) ? naming : null;
        },
        customNamePrefix() {
            return ((this.customNaming && this.customNaming.prefix) || '').trim();
        },
        customNameEntry: {
            // setup_data stores the FULL title (prefix + entry) — legacy
            // customname parity, and what certificates/cards display directly.
            // No trimming mid-flight (it would eat spaces as the student
            // types); the server normalizes on save.
            get() {
                const full = this.setupData.custom_course_name || '';
                const prefix = this.customNamePrefix;
                if (prefix && full.toLowerCase().startsWith(prefix.toLowerCase() + ' ')) {
                    return full.slice(prefix.length + 1);
                }
                if (prefix && full.toLowerCase().startsWith(prefix.toLowerCase())) {
                    return full.slice(prefix.length).replace(/^\s+/, '');
                }
                return full;
            },
            set(val) {
                const entry = (val || '').slice(0, 45);
                const prefix = this.customNamePrefix;
                this.setupData.custom_course_name = entry.trim() ? (prefix ? prefix + ' ' + entry : entry) : '';
            }
        },
        applicationLevelLabels() {
            const levels = this.setupData.application_level || [];
            return levels.map(value => {
                const level = this.applicationLevels.find(l => l.value === value);
                return level ? level.label : value;
            }).join(', ');
        },
        enrolledHours() {
            // Use max hours from linked course config, fall back to course config setting
            return this.maxHours || this.courseConfig.enrolled_hours || 20;
        },
        allocatedHours() {
            return Object.values(this.setupData.hour_allocations).reduce((sum, hours) => sum + (hours || 0), 0);
        },
        remainingHours() {
            return this.enrolledHours - this.allocatedHours;
        },
        canProceed() {
            switch (this.activeStep) {
                case 0: // Resume
                    return this.charCount('resume.background') >= 50 &&
                           this.charCount('resume.work_history') >= 50 &&
                           this.charCount('resume.importance') >= 50;
                case 1: // Overview
                    return this.totalGoals >= this.minGoals &&
                           this.totalObjectives >= this.minObjectives &&
                           this.setupData.application_level.length > 0;
                case 2: // Agenda
                    return this.remainingHours === 0 &&
                           this.allObjectivesWithText.length > 0 &&
                           this.allObjectivesWithText.every(obj => this.setupData.hour_allocations[obj.id] > 0);
                default:
                    return true;
            }
        },
        canSubmit() {
            return this.canProceed &&
                   this.charCount('evaluation_plan') >= 50 &&
                   this.setupData.reflections_agreed &&
                   (!this.customNaming || this.customNameEntry.trim().length > 0);
        }
    },
    methods: {
        charCount(path) {
            const parts = path.split('.');
            let value = this.setupData;
            for (const part of parts) {
                value = value?.[part];
            }
            return (value || '').length;
        },
        addCustomGoal() {
            this.setupData.custom_goals.push('');
        },
        removeCustomGoal(index) {
            this.setupData.custom_goals.splice(index, 1);
        },
        addCustomObjective() {
            this.setupData.custom_objectives.push('');
        },
        removeCustomObjective(index) {
            const key = 'custom_' + index;
            delete this.setupData.hour_allocations[key];
            this.setupData.custom_objectives.splice(index, 1);
        },
        initializeHourAllocations() {
            this.allObjectivesWithText.forEach((obj) => {
                if (this.setupData.hour_allocations[obj.id] === undefined) {
                    this.$set(this.setupData.hour_allocations, obj.id, 0);
                }
            });
            // Normalize any stale over-allocation (older drafts, changed hours/objectives)
            let running = 0;
            this.allObjectivesWithText.forEach((obj) => {
                const value = Number(this.setupData.hour_allocations[obj.id]) || 0;
                const allowed = Math.max(0, Math.min(value, this.enrolledHours - running));
                if (allowed !== value) {
                    this.$set(this.setupData.hour_allocations, obj.id, allowed);
                }
                running += allowed;
            });
        },
        // Hours available to this objective: the unallocated pool plus its own current value
        maxFor(objectiveId) {
            let othersTotal = 0;
            this.allObjectivesWithText.forEach(o => {
                if (o.id !== objectiveId) othersTotal += Number(this.setupData.hour_allocations[o.id]) || 0;
            });
            return Math.max(0, this.enrolledHours - othersTotal);
        },
        // Legacy-style clamp: an objective can never take more than what remains
        clampAllocation(objectiveId) {
            const current = Number(this.setupData.hour_allocations[objectiveId]) || 0;
            let clamped = Math.min(current, this.maxFor(objectiveId));
            clamped = Math.max(0, Math.round(clamped * 2) / 2);
            if (clamped !== current) {
                this.$set(this.setupData.hour_allocations, objectiveId, clamped);
            }
        },
        async load() {
            this.loading = true;
            try {
                const response = await http.get(`app/enrollments/${this.enrollmentId}/setup`, {force: true});
                this.course = response.data.course || {};
                this.serverSetupStatus = response.data.setup_status || null;
                this.maxHours = response.data.max_hours || 0;
                let config = response.data.course?.self_directed_config || {};
                // Parse if it's a JSON string
                if (typeof config === 'string') {
                    try {
                        config = JSON.parse(config);
                    } catch (e) {
                        console.error('Error parsing self_directed_config:', e);
                        config = {};
                    }
                }
                this.courseConfig = config;

                if (response.data.setup_data) {
                    const loaded = response.data.setup_data;
                    this.setupData = {
                        ...this.setupData,
                        ...loaded,
                        resume: {...this.setupData.resume, ...(loaded.resume || {})},
                        selected_goals: loaded.selected_goals || [],
                        custom_goals: loaded.custom_goals || [],
                        selected_objectives: loaded.selected_objectives || [],
                        custom_objectives: loaded.custom_objectives || [],
                        application_level: loaded.application_level || [],
                        hour_allocations: loaded.hour_allocations || {},
                        // Promoted to enrollments.custom_name server-side; the wizard
                        // still round-trips it inside setup_data on save.
                        custom_course_name: response.data.custom_name || ''
                    };
                } else if (response.data.custom_name) {
                    this.setupData.custom_course_name = response.data.custom_name;
                }

                this.initializeHourAllocations();
                this.$nextTick(() => { this.autosaveReady = true; });
            } catch (err) {
                console.error('Error loading course study plan:', err);
                this.$buefy.toast.open({
                    message: 'Error loading course study plan',
                    type: 'is-danger'
                });
            } finally {
                this.loading = false;
            }
        },
        async saveDraft() {
            if (this.saving || ['submitted', 'approved'].includes(this.setupStatus)) return;
            this.saving = true;
            try {
                // Clean up hour_allocations to only include current objectives
                const validObjectiveIds = this.allObjectivesWithText.map(o => o.id);
                const cleanedHourAllocations = {};
                for (const id of validObjectiveIds) {
                    if (this.setupData.hour_allocations[id] !== undefined) {
                        cleanedHourAllocations[id] = this.setupData.hour_allocations[id];
                    }
                }

                const saveData = {
                    ...this.setupData,
                    hour_allocations: cleanedHourAllocations,
                    stem_agreed: this.stemObjectiveSelected && this.setupData.stem_agreed === true
                };

                await http.post(`app/enrollments/${this.enrollmentId}/setup`, {
                    setup_data: saveData,
                    submit: false
                }, {force: true});

                this.lastSavedAt = new Date();
                this.saveError = false;
            } catch (err) {
                console.error('Error saving draft:', err);
                this.saveError = true;
            } finally {
                this.saving = false;
            }
        },
        async nextStep() {
            await this.saveDraft();
            if (this.activeStep === 1) {
                this.initializeHourAllocations();
            }
            this.activeStep++;
            window.scrollTo(0, 0);
        },
        prevStep() {
            this.activeStep--;
            window.scrollTo(0, 0);
        },
        async submitSetup() {
            if (!this.canSubmit) {
                this.$buefy.toast.open({
                    message: 'Please complete all required fields',
                    type: 'is-warning'
                });
                return;
            }

            // A pending autosave firing after the submit would demote the plan back to draft
            if (this.autosaveTimer) {
                clearTimeout(this.autosaveTimer);
                this.autosaveTimer = null;
            }

            this.saving = true;
            try {
                // Clean up hour_allocations to only include current objectives
                const validObjectiveIds = this.allObjectivesWithText.map(o => o.id);
                const cleanedHourAllocations = {};
                for (const id of validObjectiveIds) {
                    cleanedHourAllocations[id] = this.setupData.hour_allocations[id] || 0;
                }

                const submitData = {
                    ...this.setupData,
                    hour_allocations: cleanedHourAllocations,
                    stem_agreed: this.stemObjectiveSelected && this.setupData.stem_agreed === true
                };

                const response = await http.post(`app/enrollments/${this.enrollmentId}/setup`, {
                    setup_data: submitData,
                    submit: true
                }, {force: true});

                this.setupData = {
                    ...response.data.setup_data,
                    custom_course_name: response.data.custom_name || ''
                };
                this.serverSetupStatus = response.data.setup_status || this.setupData?.status || 'submitted';
                // Keep the store enrollment in sync so CourseSideNav and Welcome update immediately
                if (this.enrollment) this.$set(this.enrollment, 'setup_status', this.serverSetupStatus);

                this.$buefy.toast.open({
                    message: 'Course study plan submitted for approval',
                    type: 'is-success',
                    duration: 5000
                });

                this.$emit('submitted');
            } catch (err) {
                console.error('Error submitting course study plan:', err);
                const errors = err.response?.data?.errors || [];
                const message = errors.length > 0 ? errors.join(', ') : 'Error submitting course study plan';
                this.$buefy.toast.open({
                    message,
                    type: 'is-danger',
                    duration: 5000
                });
            } finally {
                this.saving = false;
            }
        },
        formatDate(date) {
            if (!date) return '';
            return moment(date).format('MMMM Do, YYYY [at] h:mm A');
        }
    },
    watch: {
        'setupData.selected_objectives': {
            handler() {
                this.initializeHourAllocations();
            },
            deep: true
        },
        'setupData.custom_objectives': {
            handler() {
                this.initializeHourAllocations();
            },
            deep: true
        },
        'setupData': {
            handler() {
                if (!this.autosaveReady || ['submitted', 'approved'].includes(this.setupStatus)) return;
                if (!this.autosaveTimer) {
                    this.autosaveTimer = setTimeout(() => {
                        this.autosaveTimer = null;
                        this.saveDraft();
                    }, 2000);
                }
            },
            deep: true
        }
    },
    mounted() {
        this.load();
    },
    beforeDestroy() {
        if (this.autosaveTimer) {
            clearTimeout(this.autosaveTimer);
            this.autosaveTimer = null;
            if (!['submitted', 'approved'].includes(this.setupStatus)) this.saveDraft();
        }
    }
};
</script>

<style lang="scss" scoped>
.study-plan-content-frame {
    background-color: #fff;
    border-radius: 8px;
    padding: 2rem;
    margin-bottom: 1.5rem;
}

/* Flat replacement for Bulma's .box — same padding/radius, no shadow */
.flat-box {
    border-radius: 6px;
    padding: 1.25rem;
}

/* Divides the wizard's main content sections (goals, objectives, application
   level, etc.) from one another — plain mb-5 spacing read as too cramped */
.wizard-section {
    margin-bottom: 1.75rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid #eee;

    &:last-child {
        margin-bottom: 0;
        padding-bottom: 0;
        border-bottom: none;
    }
}

.study-plan-header {
    margin-bottom: 1.5rem;
    border: none;

    h2 {
        margin-top: 0.5rem;
        margin-bottom: 1rem;
        border: none;
        border-bottom: none;
    }

    > div {
        border: none;
    }

    ::v-deep .progress-wrapper {
        border: none;
    }
}

.step-content {
    padding-bottom: 2rem;
}

/* Readable but compact — between Bulma's size 6 and the old size-7 fine print */
.stem-info-box {
    font-size: 0.875rem;
}

/* Multi-line label: keep the check glyph on the first line, not centered */
.agreement-checkbox {
    align-items: flex-start;

    ::v-deep .check {
        margin-top: 3px;
    }
}

.content-controls {
    margin-top: 2rem;
    padding-top: 1.25rem;
    border-top: 1px solid #ededed;
    font-size: 0.85rem;
}

.box {
    border-radius: 8px;
}

.approved-content {
    padding-bottom: 2rem;

    // The page root's Bulma .content typography bleeds into the tabs nav ul
    // (2em left margin + bullets) — reset it so the tabs align with the card
    ::v-deep .tabs ul {
        margin: 0;
        list-style: none;
    }

    // Line the tab panel content up with the tab label (Bulma tab links
    // carry 1em horizontal padding, so the icon sits 1em in from the edge)
    ::v-deep .tab-content {
        padding-top: 2.25rem;
        padding-left: 1em;
        padding-right: 1em;
    }

    .ai-content {
        line-height: 1.7;

        ::v-deep h4 {
            color: #363636;
            font-weight: 600;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
        }

        ::v-deep h5 {
            color: #4a4a4a;
            font-weight: 600;
            margin-top: 1.25rem;
            margin-bottom: 0.5rem;
        }

        ::v-deep ul, ::v-deep ol {
            margin-left: 1.5rem;
            margin-bottom: 1rem;
        }

        ::v-deep li {
            margin-bottom: 0.5rem;
        }

        ::v-deep p {
            margin-bottom: 1rem;
        }
    }

    .plan-summary {
        h5 {
            color: #363636;
            font-weight: 600;
            border-bottom: 1px solid #eee;
            padding-bottom: 0.5rem;
        }

        ul {
            margin-left: 1.5rem;
        }

        li {
            margin-bottom: 0.5rem;
        }
    }
}
</style>
